import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { getAircraftTypeIcons, assignAircraftTypeIcon, assignAircraftTypeIconBulk } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';
import './Admin.css';

const SPRITE_COLS = 8;
const SPRITE_ROWS = 16;

// Match the live aircraft-table rendering: backgroundSize = N_COLS × cell
// in width with `auto` height so the sheet's natural aspect ratio is
// preserved, and a brightness filter renders the silhouette black against
// the light admin table background.
function spriteCellStyle(row, col, size = 48) {
  return {
    display: 'inline-block',
    width: `${size}px`,
    height: `${size}px`,
    backgroundImage: `url(${spriteImage})`,
    backgroundSize: `${size * SPRITE_COLS}px auto`,
    backgroundPosition: `-${col * size}px -${row * size}px`,
    backgroundRepeat: 'no-repeat',
    filter: 'brightness(0) saturate(100%) brightness(0.3)'
  };
}

// Group rows by `family` while preserving the server-supplied order
// (server sorts by family-total DESC then family ASC then count DESC).
function groupByFamily(rows) {
  const groups = [];
  const byKey = new Map();
  rows.forEach(r => {
    const key = r.family || r.typeDesignator;
    if (!byKey.has(key)) {
      const g = { family: key, types: [], totalCount: 0, anyAssigned: false };
      byKey.set(key, g);
      groups.push(g);
    }
    const g = byKey.get(key);
    g.types.push(r);
    g.totalCount += r.aircraftCount || 0;
    if (r.spriteRow != null && r.spriteCol != null) g.anyAssigned = true;
  });
  return groups;
}

function AdminTypeIcons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  // Picker target — either { kind: 'type', typeDesignator } or { kind: 'family', family, typeDesignators }
  const [picker, setPicker] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      // Always fetch the full set so the family group is complete; filter
      // the "unassigned only" view in the client so a family with one
      // missing variant still surfaces alongside its assigned siblings.
      const resp = await getAircraftTypeIcons({ unassignedOnly: false }, token);
      if (isApiSuccess(resp)) {
        setRows(resp.rows || []);
      } else {
        setError(getFirstApiError(resp) || 'Failed to load type icons');
      }
    } catch (e) {
      setError('Failed to load type icons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const allGroups = groupByFamily(rows);
    if (!unassignedOnly) return allGroups;
    // "Show only types without an icon": a family is shown if at least one
    // of its variants is unassigned. Within such a family we still render
    // the assigned siblings (greyed) so the operator can see what's
    // already mapped before bulk-applying.
    return allGroups.filter(g => g.types.some(t => t.spriteRow == null || t.spriteCol == null));
  }, [rows, unassignedOnly]);

  // Build a (row,col) → list-of-types lookup so picker tooltips show what
  // is already assigned to each cell. Many type designators can share a
  // sprite cell (e.g. all 737 variants → same Boeing 737 silhouette).
  const cellLabels = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      if (r.spriteRow == null || r.spriteCol == null) return;
      const key = `${r.spriteRow}-${r.spriteCol}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r.typeDesignator);
    });
    return map;
  }, [rows]);

  const titleFor = (r, c) => {
    const list = cellLabels.get(`${r}-${c}`);
    if (!list || list.length === 0) return `Row ${r}, Col ${c} — unassigned`;
    if (list.length <= 8) return `Row ${r}, Col ${c}: ${list.join(', ')}`;
    return `Row ${r}, Col ${c}: ${list.slice(0, 8).join(', ')} +${list.length - 8} more`;
  };

  const assignSingle = async (typeDesignator, row, col) => {
    try {
      const token = getAccessToken();
      const resp = await assignAircraftTypeIcon({ typeDesignator, spriteRow: row, spriteCol: col }, token);
      if (isApiSuccess(resp)) {
        setActionMsg({ type: 'success', text: `Assigned ${typeDesignator} → [${row},${col}]` });
        setPicker(null);
        load();
      } else {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Assign failed' });
      }
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Assign failed' });
    }
  };

  const assignFamily = async (typeDesignators, row, col, family) => {
    try {
      const token = getAccessToken();
      const resp = await assignAircraftTypeIconBulk({
        typeDesignators, spriteRow: row, spriteCol: col,
        notes: `bulk: ${family}`
      }, token);
      if (isApiSuccess(resp)) {
        setActionMsg({ type: 'success', text: `Assigned ${resp.assignedCount || typeDesignators.length} ${family} variants → [${row},${col}]` });
        setPicker(null);
        load();
      } else {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Bulk assign failed' });
      }
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Bulk assign failed' });
    }
  };

  if (loading) return <div className="admin-loading">Loading type icons...</div>;
  if (error) {
    return (
      <div className="admin-error">
        <p>{error}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }

  const totalTypes = rows.length;
  const totalUnassigned = rows.filter(r => r.spriteRow == null || r.spriteCol == null).length;

  return (
    <div className="admin-table-container">
      <div className="admin-toolbar">
        <label>
          <input type="checkbox" checked={unassignedOnly} onChange={e => setUnassignedOnly(e.target.checked)} />
          Show only families with at least one unmapped variant
        </label>
        <button onClick={load}>Refresh</button>
        <span className="admin-toolbar-meta">
          {totalUnassigned} / {totalTypes} types unmapped, {groups.length} {unassignedOnly ? 'families with gaps' : 'families'}
        </span>
      </div>

      {actionMsg && (
        <div className={`admin-action-message admin-action-${actionMsg.type}`}>
          {actionMsg.text}
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Manufacturer/Model</th>
            <th>Aircraft</th>
            <th>Icon</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {groups.map(group => {
            const familyKey = group.family;
            const familyTypeList = group.types.map(t => t.typeDesignator);
            const isFamilyPicking = picker && picker.kind === 'family' && picker.family === familyKey;
            return (
              <>
                <tr key={`fam:${familyKey}`} className="admin-family-header-row">
                  <td colSpan={3}>
                    <strong>{familyKey}</strong>
                    <span className="admin-family-meta">
                      {' '}— {group.types.length} variant{group.types.length === 1 ? '' : 's'}, {group.totalCount} aircraft
                    </span>
                  </td>
                  <td colSpan={2}>
                    <button
                      onClick={() => setPicker(isFamilyPicking ? null : { kind: 'family', family: familyKey, typeDesignators: familyTypeList })}
                    >
                      {isFamilyPicking ? 'Cancel' : `Pick icon for all ${group.types.length} variants`}
                    </button>
                  </td>
                </tr>
                {isFamilyPicking && (
                  <tr key={`fam:${familyKey}:picker`} className="receivers-row">
                    <td colSpan={5}>
                      <div className="admin-sprite-picker-hint">
                        Click a sprite cell to apply to <strong>all {group.types.length} variants</strong> in {familyKey}:
                        {' '}{familyTypeList.join(', ')}
                      </div>
                      <div className="admin-sprite-picker">
                        {Array.from({ length: SPRITE_ROWS }).map((_, r) => (
                          <div key={r} className="admin-sprite-row">
                            {Array.from({ length: SPRITE_COLS }).map((_, c) => (
                              <button
                                key={`${r}-${c}`}
                                className="admin-sprite-cell"
                                title={titleFor(r, c)}
                                onClick={() => assignFamily(familyTypeList, r, c, familyKey)}
                                style={spriteCellStyle(r, c, 48)}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                {group.types.map(row => {
                  const isTypePicking = picker && picker.kind === 'type' && picker.typeDesignator === row.typeDesignator;
                  const hasIcon = row.spriteRow != null && row.spriteCol != null;
                  return (
                    <>
                      <tr key={row.typeDesignator} className={isTypePicking ? 'expanded' : ''}>
                        <td data-label="Type"><strong>{row.typeDesignator}</strong></td>
                        <td data-label="Mfr/Model">{row.manufacturerModel || <span className="gap">—</span>}</td>
                        <td data-label="Aircraft">{row.aircraftCount}</td>
                        <td data-label="Icon">
                          {hasIcon
                            ? <div style={spriteCellStyle(row.spriteRow, row.spriteCol, 32)} />
                            : <span className="gap">none</span>}
                        </td>
                        <td>
                          <button onClick={() => setPicker(isTypePicking ? null : { kind: 'type', typeDesignator: row.typeDesignator })}>
                            {isTypePicking ? 'Cancel' : (hasIcon ? 'Change' : 'Pick icon')}
                          </button>
                        </td>
                      </tr>
                      {isTypePicking && (
                        <tr key={`${row.typeDesignator}:picker`} className="receivers-row">
                          <td colSpan={5}>
                            <div className="admin-sprite-picker-hint">
                              Click a sprite cell to apply to <strong>{row.typeDesignator}</strong> only:
                            </div>
                            <div className="admin-sprite-picker">
                              {Array.from({ length: SPRITE_ROWS }).map((_, r) => (
                                <div key={r} className="admin-sprite-row">
                                  {Array.from({ length: SPRITE_COLS }).map((_, c) => (
                                    <button
                                      key={`${r}-${c}`}
                                      className="admin-sprite-cell"
                                      title={titleFor(r, c)}
                                      onClick={() => assignSingle(row.typeDesignator, r, c)}
                                      style={spriteCellStyle(r, c, 48)}
                                    />
                                  ))}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AdminTypeIcons;
